import { reactive } from 'vue';

// Quando cada FAMÍLIA de evento realtime foi entregue pela última vez.
//
// É o que permite ao refetch de segurança (useSafetyRefetch) distinguir
// "o dado mudou e o canal avisou" de "o dado mudou e o canal NÃO avisou": o
// segundo caso é o canal morto — o socket diz conectado, os pings chegam, e
// nenhum broadcast é entregue (o modo de falha do app/services/cable/watchdog.rb).
// Sem este relógio, divergência entre servidor e tela é indistinguível de
// "nada aconteceu no intervalo".
//
// Módulo singleton de propósito: um conector ActionCable por aba, e quem lê é
// um timer — não precisa de Vuex nem de reatividade pra ler o instante.
// Só `deadChannelCounts` é reativo, pra quem quiser mostrar o contador.

export const REALTIME_FAMILIES = Object.freeze({
  CONVERSATIONS: 'conversations',
  MESSAGES: 'messages',
});

const { CONVERSATIONS, MESSAGES } = REALTIME_FAMILIES;

// Eventos que provam que a família está viva. Mais abrangente é mais seguro:
// incluir um evento a mais só deixa o detector menos sensível, nunca o faz
// acusar canal morto à toa. `message.created` também mexe na lista (sobe o
// last_activity_at da conversa).
export const EVENT_FAMILIES = Object.freeze({
  'message.created': [CONVERSATIONS, MESSAGES],
  'message.updated': [MESSAGES],
  'conversation.created': [CONVERSATIONS],
  'conversation.status_changed': [CONVERSATIONS],
  'conversation.updated': [CONVERSATIONS],
  'conversation.read': [CONVERSATIONS],
  'conversation.contact_changed': [CONVERSATIONS],
  'assignee.changed': [CONVERSATIONS],
  'team.changed': [CONVERSATIONS],
  'contact.deleted': [CONVERSATIONS],
  // Agente ganhou/perdeu caixa: a contagem da assinatura da lista muda sem
  // nenhum evento de conversa — sem isto, virava "canal morto" falso.
  'inbox.membership_changed': [CONVERSATIONS],
});

const lastDelivery = {};
let lastTransportChange = null;

export const deadChannelCounts = reactive({
  [CONVERSATIONS]: 0,
  [MESSAGES]: 0,
});

export const recordDelivery = (eventName, now = Date.now()) => {
  (EVENT_FAMILIES[eventName] || []).forEach(family => {
    lastDelivery[family] = now;
  });
};

export const lastDeliveryAt = family => lastDelivery[family] ?? null;

// Queda/volta do socket é falha CONHECIDA, tratada pelo ReconnectService: no
// intervalo em que ela acontece, divergência não é "canal morto" e o detector
// não dá veredito.
export const recordTransportChange = (now = Date.now()) => {
  lastTransportChange = now;
};

export const lastTransportChangeAt = () => lastTransportChange;

export const recordDeadChannel = family => {
  deadChannelCounts[family] = (deadChannelCounts[family] || 0) + 1;
  return deadChannelCounts[family];
};

// Embrulha os handlers do conector: o instante é gravado ANTES do handler
// (se ele lançar, o evento foi entregue mesmo assim) e só para evento que
// passou pelo isAValidEvent (o onReceived só chama handler válido).
export const trackDeliveries = events =>
  Object.fromEntries(
    Object.entries(events).map(([eventName, handler]) => {
      if (!EVENT_FAMILIES[eventName]) return [eventName, handler];
      return [
        eventName,
        data => {
          recordDelivery(eventName);
          return handler(data);
        },
      ];
    })
  );

// Só pros specs.
export const resetRealtimeDelivery = () => {
  Object.keys(lastDelivery).forEach(key => delete lastDelivery[key]);
  lastTransportChange = null;
  Object.keys(deadChannelCounts).forEach(key => {
    deadChannelCounts[key] = 0;
  });
};
